import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CreditCard, ArrowRight, Loader2, Shield, CheckCircle2, Package, Tag, X } from 'lucide-react';
import { formatPrice } from '../data/pricing';
import { trackCheckoutStarted, trackCouponApplied, trackRecCheckoutStarted } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.645h6.563c2.175 0 3.74.58 4.65 1.724.426.535.698 1.145.811 1.814.118.696.12 1.544-.006 2.593l-.013.085v.754l.588.332a3.5 3.5 0 0 1 1.182 1.05c.398.567.652 1.27.756 2.088.107.84.072 1.833-.104 2.95a8.9 8.9 0 0 1-.93 2.85 5.7 5.7 0 0 1-1.502 1.826c-.604.476-1.32.827-2.128 1.044a9.9 9.9 0 0 1-2.653.33h-.63a1.9 1.9 0 0 0-1.184.413 1.9 1.9 0 0 0-.655 1.105l-.032.166-.547 3.464-.025.12a.2.2 0 0 1-.056.11.16.16 0 0 1-.103.04Z" fill="#253B80"/>
    <path d="M19.542 9.318a7 7 0 0 0-.086-.493c-.96-4.917-6.324-4.917-6.324-4.917H6.471L3.592 21.176a.42.42 0 0 0 .415.487h4.2l1.055-6.682a1.24 1.24 0 0 1 1.223-1.044h2.549c5.005 0 6.074-3.842 6.508-6.619Z" fill="#179BD7"/>
    <path d="M8.047 9.15a.77.77 0 0 1 .757-.644h4.783s2.912.048 4.192 1.98c-1.032-5.288-5.312-5.358-5.312-5.358H7.12a.77.77 0 0 0-.757.645L3.592 21.177a.42.42 0 0 0 .415.486h4.2l1.055-6.682L8.047 9.15Z" fill="#222D65"/>
  </svg>
);

export const BundlePurchaseDialog = ({ bundle, open, onOpenChange, currency = 'usd' }) => {
  const { isShopifyBilled, shopifyBilling, isPro } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [error, setError] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');

  if (!bundle) return null;

  const basePrice = currency === 'nok' ? bundle.bundle_price_nok : bundle.bundle_price_usd;
  const finalPrice = couponResult ? couponResult.final_price : basePrice;
  const savings = currency === 'nok' ? bundle.savings_nok : bundle.savings_usd;
  const priceDisplay = formatPrice(finalPrice, currency);
  const totalValue = formatPrice(savings?.individual_total || 0, currency);

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponResult(null);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        bundle_slug: bundle.slug,
        amount: basePrice,
        currency,
      });
      setCouponResult(res.data);
      trackCouponApplied(bundle.slug, res.data.code, res.data.discount_amount);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponResult(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleStripeCheckout = async () => {
    setPaymentMethod('stripe');
    setCheckoutLoading(true);
    setError('');
    trackCheckoutStarted(bundle.slug, 'bundle', finalPrice, currency, 'stripe');
    // Attribution
    try {
      const attr = JSON.parse(sessionStorage.getItem('rec_attribution') || 'null');
      if (attr && attr.slug === bundle.slug && (Date.now() - attr.clicked_at) < 1800000) {
        trackRecCheckoutStarted(bundle.slug, 'bundle', attr.placement, finalPrice, currency);
      }
    } catch { /* attribution non-critical */ }
    try {
      const res = await axios.post(`${API}/purchases/bundle-checkout`, {
        bundle_slug: bundle.slug,
        origin_url: window.location.origin,
        currency,
        coupon_code: couponResult?.code || null,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Checkout failed';
      setError(msg);
      setCheckoutLoading(false);
      setPaymentMethod(null);
    }
  };

  const handlePayPalCheckout = async () => {
    setPaymentMethod('paypal');
    setCheckoutLoading(true);
    setError('');
    trackCheckoutStarted(bundle.slug, 'bundle', finalPrice, currency, 'paypal');
    // Attribution
    try {
      const attr = JSON.parse(sessionStorage.getItem('rec_attribution') || 'null');
      if (attr && attr.slug === bundle.slug && (Date.now() - attr.clicked_at) < 1800000) {
        trackRecCheckoutStarted(bundle.slug, 'bundle', attr.placement, finalPrice, currency);
      }
    } catch { /* attribution non-critical */ }
    try {
      const res = await axios.post(`${API}/purchases/bundle-checkout/paypal`, {
        bundle_slug: bundle.slug,
        currency,
        return_url: `${window.location.origin}/payment/success?type=bundle&provider=paypal&bundle=${bundle.slug}`,
        cancel_url: `${window.location.origin}/?cancelled=${bundle.slug}`,
        coupon_code: couponResult?.code || null,
      });
      if (res.data.approve_url) {
        window.location.href = res.data.approve_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'PayPal checkout failed';
      setError(msg);
      setCheckoutLoading(false);
      setPaymentMethod(null);
    }
  };

  const handleClose = (o) => {
    if (!checkoutLoading) {
      onOpenChange(o);
      setPaymentMethod(null);
      setError('');
      setCouponResult(null);
      setCouponCode('');
      setCouponError('');
      setShowCoupon(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border-stone-100 p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="bundle-purchase-dialog">
        {isShopifyBilled ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#F0F7F0] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-[#5E8E3E]" />
            </div>
            <h3 className="text-lg font-serif font-bold text-stone-800 mb-2">{bundle.title}</h3>
            {isPro ? (
              <>
                <p className="text-sm text-stone-500 mb-4">This collection is included with your Shopify Pro plan. You have full access to all products.</p>
                <button onClick={() => handleClose(false)} className="w-full bg-[#5E8E3E] text-white rounded-full py-3 font-semibold text-sm hover:bg-[#4E7E2E] transition-colors">
                  Continue
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-stone-500 mb-4">Upgrade to Lumera Pro through your Shopify admin to access all product collections.</p>
                <button onClick={() => window.open(`https://${shopifyBilling.shopDomain}/admin/settings/billing`, '_blank')} className="w-full bg-[#5E8E3E] text-white rounded-full py-3 font-semibold text-sm hover:bg-[#4E7E2E] transition-colors mb-2">
                  Upgrade in Shopify
                </button>
                <button onClick={() => handleClose(false)} className="w-full text-stone-400 text-xs py-2">Cancel</button>
              </>
            )}
          </div>
        ) : (
        <>
        <div className="bg-stone-900 px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-xl flex items-center gap-2">
              <Package className="w-5 h-5 text-[#B49A88]" />
              {bundle.title}
            </DialogTitle>
            <DialogDescription className="text-stone-400 font-sans text-sm mt-1">
              {bundle.product_count || bundle.products?.length || 0} products &middot; {priceDisplay}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-4">
          {/* Bundle contents */}
          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-[10px] font-sans font-bold uppercase tracking-widest text-stone-400 mb-3">Included in this collection</p>
            <div className="space-y-2.5">
              {bundle.products?.map((product) => (
                <div key={product.slug} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm font-sans text-stone-700">{product.title}</span>
                  </div>
                  <span className="text-xs font-sans text-stone-400 line-through">
                    {formatPrice(currency === 'nok' ? product.price_nok : product.price_usd, currency)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-stone-200 flex items-center justify-between">
              <span className="text-xs font-sans text-stone-500">Total value: <span className="line-through">{totalValue}</span></span>
              <span className="text-base font-serif font-bold text-[#111111]">Collection price: {priceDisplay}</span>
            </div>
            {savings?.savings_percent > 0 && (
              <div className="mt-2 text-center">
                <span className="inline-block text-[10px] font-sans font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  You save {formatPrice(savings.savings, currency)} ({savings.savings_percent}%)
                </span>
              </div>
            )}
          </div>

          {/* Commercial License notice */}
          <div data-testid="bundle-license-notice" className="flex items-center gap-2.5 bg-[#F5F0EB] border border-[#EDE3D6] rounded-xl px-4 py-3">
            <Shield className="w-4 h-4 text-[#8B7B6B] flex-shrink-0" />
            <div>
              <p className="text-xs font-sans font-semibold text-[#4A3525]">Commercial License included</p>
              <p className="text-[11px] font-sans text-[#8A7D72]">All products in this collection include full commercial rights.</p>
            </div>
          </div>

          {/* Coupon section */}
          {couponResult ? (
            <div data-testid="bundle-coupon-applied" className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-sans font-semibold text-emerald-700">{couponResult.code}</span>
                <span className="text-xs font-sans text-emerald-600">
                  -{formatPrice(couponResult.discount_amount, currency)}
                </span>
              </div>
              <button onClick={handleRemoveCoupon} className="text-stone-400 hover:text-stone-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : !showCoupon ? (
            <button
              data-testid="bundle-show-coupon-btn"
              onClick={() => setShowCoupon(true)}
              className="text-xs font-sans text-stone-400 hover:text-stone-600 transition-colors flex items-center gap-1.5"
            >
              <Tag className="w-3 h-3" /> Have a discount code?
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                data-testid="bundle-coupon-input"
                type="text"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                placeholder="Enter code"
                className="flex-1 px-3 py-2 text-xs font-sans rounded-lg border border-stone-200 focus:border-stone-400 focus:outline-none bg-white"
                onKeyDown={(e) => { if (e.key === 'Enter') handleValidateCoupon(); }}
              />
              <button
                data-testid="bundle-apply-coupon-btn"
                onClick={handleValidateCoupon}
                disabled={couponLoading || !couponCode.trim()}
                className="px-3 py-2 text-xs font-sans font-semibold bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 transition-colors"
              >
                {couponLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
              </button>
            </div>
          )}
          {couponError && (
            <p data-testid="bundle-coupon-error" className="text-xs font-sans text-red-500 -mt-1">{couponError}</p>
          )}

          {error && (
            <div data-testid="bundle-purchase-error" className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {/* Payment methods */}
          <button
            data-testid="bundle-stripe-btn"
            onClick={handleStripeCheckout}
            disabled={checkoutLoading}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
              paymentMethod === 'stripe' ? 'border-[#B49A88] bg-[#B49A88]/5' : 'border-stone-100 hover:border-stone-300 bg-white'
            } ${checkoutLoading && paymentMethod !== 'stripe' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-[#635BFF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-stone-800 text-sm">Card</p>
              <p className="font-sans text-xs text-stone-500 mt-0.5">Visa, Mastercard, Amex &mdash; via Stripe</p>
            </div>
            {checkoutLoading && paymentMethod === 'stripe' ? (
              <Loader2 className="w-5 h-5 text-[#B49A88] animate-spin flex-shrink-0" />
            ) : (
              <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
            )}
          </button>

          <button
            data-testid="bundle-paypal-btn"
            onClick={handlePayPalCheckout}
            disabled={checkoutLoading}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
              paymentMethod === 'paypal' ? 'border-[#B49A88] bg-[#B49A88]/5' : 'border-stone-100 hover:border-stone-300 bg-white'
            } ${checkoutLoading && paymentMethod !== 'paypal' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#0070BA]/10 flex items-center justify-center flex-shrink-0">
              <PayPalIcon />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-stone-800 text-sm">PayPal</p>
              <p className="font-sans text-xs text-stone-500 mt-0.5">Pay with PayPal balance or card</p>
            </div>
            {checkoutLoading && paymentMethod === 'paypal' ? (
              <Loader2 className="w-5 h-5 text-[#B49A88] animate-spin flex-shrink-0" />
            ) : (
              <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
            )}
          </button>

          <div className="flex items-center justify-center gap-2 pt-2">
            <Shield className="w-3.5 h-3.5 text-stone-400" />
            <p className="text-[11px] font-sans text-stone-400">Secure, encrypted payment. Lifetime access.</p>
          </div>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
